(function (root) {
  'use strict';

  function discountError(code, message) {
    var error = new Error(message);
    error.code = code;
    return error;
  }

  function normalizedCode(value) {
    var raw = String(value == null ? '' : value);
    if (/[\u0000-\u001f\u007f,]/.test(raw)) {
      throw discountError('invalid-input', 'Enter one discount code at a time.');
    }
    var code = raw.trim();
    if (!code) throw discountError('empty', 'Enter a discount code.');
    return code;
  }

  function sameCode(left, right) {
    return left.toLowerCase() === right.toLowerCase();
  }

  function cartCodes(cart) {
    return cart.discount_codes.reduce(function (codes, entry) {
      if (!codes.some(function (code) { return sameCode(code, entry.code); })) codes.push(entry.code);
      return codes;
    }, []);
  }

  function createDiscountService(options) {
    var pending = false;
    var fetchImpl = options.fetchImpl || root.fetch.bind(root);

    async function request(url, init) {
      var response;
      try {
        response = await fetchImpl(url, Object.assign({credentials: 'same-origin', cache: 'no-store',
          signal: options.signal, headers: {Accept: 'application/json'}}, init));
      } catch (error) {
        throw discountError('network', 'Unable to update discounts. Please try again.');
      }
      if (!response.ok) throw discountError('network', 'Unable to update discounts. Please try again.');
      var cart;
      try { cart = await response.json(); }
      catch (error) { throw discountError('response', 'Unable to verify discounts. Please try again.'); }
      if (!cart || !Array.isArray(cart.items) || !Array.isArray(cart.discount_codes)
        || cart.discount_codes.some(function (entry) {
          return !entry || typeof entry.code !== 'string' || !entry.code.trim()
            || /[\u0000-\u001f\u007f,]/.test(entry.code) || typeof entry.applicable !== 'boolean';
        })) {
        throw discountError('response', 'Unable to verify discounts. Please try again.');
      }
      return cart;
    }

    function read() { return request(options.routes.cart); }

    async function mutate(action, input) {
      if (pending) throw discountError('pending', 'A discount update is already in progress.');
      var code = normalizedCode(input);
      pending = true;
      var changed = false;
      async function write(codes) {
        changed = true;
        if (options.onMutation) options.onMutation();
        await request(options.routes.update, {method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({discount: codes.join(',')})});
        return read();
      }
      try {
        var initial = await read();
        var existing = initial.discount_codes.find(function (entry) { return sameCode(entry.code, code); });
        var codes = cartCodes(initial);
        if (action === 'apply' && existing && existing.applicable) {
          return {status: 'duplicate', cart: initial, changed: false};
        }
        if (action === 'remove') {
          var remaining = codes.filter(function (entry) { return !sameCode(entry, code); });
          var removed = existing ? await write(remaining) : initial;
          if (removed.discount_codes.some(function (entry) { return sameCode(entry.code, code); })) {
            throw discountError('response', 'Unable to remove this discount. Please try again.');
          }
          return {status: 'removed', cart: removed, changed: changed};
        }
        if (!existing) codes.push(code);
        var updated = await write(codes);
        var applied = updated.discount_codes.find(function (entry) { return sameCode(entry.code, code); });
        var preservesAcceptedCodes = initial.discount_codes.filter(function (entry) { return entry.applicable; })
          .every(function (entry) {
            return updated.discount_codes.some(function (current) {
              return current.applicable && sameCode(current.code, entry.code);
            });
          });
        if (applied && applied.applicable && preservesAcceptedCodes) {
          return {status: 'applied', cart: updated, changed: true};
        }
        if (!existing || !preservesAcceptedCodes) {
          var latest = await read();
          if (latest.discount_codes.some(function (entry) { return sameCode(entry.code, code); })) {
            updated = await write(cartCodes(latest).filter(function (entry) { return !sameCode(entry, code); }));
          } else updated = latest;
        }
        return {status: 'invalid', cart: updated, changed: changed};
      } catch (error) {
        error.changed = changed;
        throw error;
      } finally {
        pending = false;
      }
    }

    return {read: read, apply: function (code) { return mutate('apply', code); },
      remove: function (code) { return mutate('remove', code); },
      get pending() { return pending; }};
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {createDiscountService: createDiscountService};
  }

  if (!root.customElements || root.customElements.get('km-cart-discount')) return;

  class CartDiscount extends root.HTMLElement {
    constructor() {
      super();
      this.pending = false;
      this.refreshBlocked = false;
      this.readRevision = 0;
      this.lifecycle = 0;
      this.onClick = event => {
        if (event.target.closest('[data-km-discount-retry]')) {
          event.preventDefault();
          this.retryRefresh();
          return;
        }
        var remove = event.target.closest('[data-km-discount-remove]');
        if (remove && this.contains(remove)) {
          event.preventDefault();
          this.perform('remove', remove.dataset.kmDiscountRemove);
        } else if (event.target.closest('[data-km-discount-apply]')) {
          event.preventDefault();
          this.perform('apply', this.input.value);
        }
      };
      this.onKeydown = event => {
        if (event.target !== this.input || event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        this.perform('apply', this.input.value);
      };
      this.onCartChanged = event => {
        if (event.type === 'cart:rendered') {
          var form = this.closest('form');
          var location = form && form.dataset.location || 'cart-drawer';
          if (event.detail && event.detail.location && event.detail.location !== location) return;
          var nativeCart = form && form.kmCartForm;
          if (!nativeCart || !nativeCart.failedCartOperations || !nativeCart.failedCartOperations.has('discount')) this.clearRefreshBlock();
        }
        this.refresh();
      };
      this.onSubmit = event => {
        if ((!this.pending && !this.refreshBlocked) || event.target !== this.closest('form')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.message(this.pending ? 'Please wait for the discount update to finish.'
          : 'Please refresh your cart before checkout.', this.refreshBlocked);
      };
    }

    connectedCallback() { this.activate(); }

    disconnectedCallback() { this.deactivate(); }

    activate() {
      if (this.active || !this.isConnected) return;
      this.input = this.querySelector('[data-km-discount-input]');
      this.applyButton = this.querySelector('[data-km-discount-apply]');
      this.codes = this.querySelector('[data-km-discount-codes]');
      this.status = this.querySelector('[data-km-discount-status]');
      this.retryButton = this.querySelector('[data-km-discount-retry]');
      if (!this.input || !this.applyButton || !this.codes || !this.status) return;
      this.active = true;
      this.lifecycle++;
      this.controller = new root.AbortController();
      this.service = createDiscountService({routes: {cart: this.dataset.cartUrl,
        update: this.dataset.updateUrl}, signal: this.controller.signal,
        onMutation: () => { this.operationChanged = true; }});
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      root.addEventListener('submit', this.onSubmit, true);
      this.cartEvents = ['cart:updated', 'cart:rendered', 'ajaxProduct:added', 'drawerOpen.CartDrawer'];
      this.cartEvents.forEach(name => document.addEventListener(name, this.onCartChanged));
      this.setPending(false);
      this.refresh();
    }

    deactivate() {
      if (!this.active) return;
      this.active = false;
      this.lifecycle++;
      this.readRevision++;
      this.controller.abort();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      root.removeEventListener('submit', this.onSubmit, true);
      this.cartEvents.forEach(name => document.removeEventListener(name, this.onCartChanged));
      this.refreshNeeded = false;
      this.clearRefreshBlock();
      this.setPending(false);
    }

    setPending(value) {
      this.pending = value;
      this.setAttribute('aria-busy', String(value));
      this.input.disabled = value;
      this.applyButton.disabled = value;
      if (this.retryButton) this.retryButton.disabled = value;
      this.querySelectorAll('[data-km-discount-remove]').forEach(button => { button.disabled = value; });
    }

    message(text, error) {
      this.status.textContent = text;
      this.status.hidden = !text;
      this.status.classList.toggle('is-error', !!error);
      this.input.setAttribute('aria-invalid', String(!!error));
    }

    clearRefreshBlock() {
      var blocked = this.refreshBlocked;
      this.refreshBlocked = false;
      if (this.retryButton) this.retryButton.hidden = true;
      if (blocked) this.message('', false);
    }

    blockRefresh() {
      this.refreshBlocked = true;
      if (this.retryButton) this.retryButton.hidden = false;
      this.message('Unable to refresh your cart. Please try again.', true);
    }

    async retryRefresh() {
      if (!this.active || this.pending || !this.refreshBlocked) return;
      var form = this.closest('form');
      var nativeCart = form && form.kmCartForm;
      var operation = nativeCart && nativeCart.beginOperation && nativeCart.beginOperation('discount');
      var succeeded = false;
      var lifecycle = this.lifecycle;
      this.setPending(true);
      this.message('Refreshing cart...', false);
      try {
        document.dispatchEvent(new root.CustomEvent('cart:build'));
        if (nativeCart && nativeCart.cartBuildPromise) await nativeCart.cartBuildPromise;
        else if (this.refreshBlocked) throw new Error('Cart refresh unavailable');
        if (!this.active || lifecycle !== this.lifecycle) return;
        this.clearRefreshBlock();
        succeeded = true;
        this.message('Cart refreshed.', false);
      } catch (error) {
        if (this.active && lifecycle === this.lifecycle) this.blockRefresh();
      } finally {
        if (operation) nativeCart.finishOperation(operation, succeeded);
        if (this.active && lifecycle === this.lifecycle) {
          this.setPending(false);
          if (this.refreshNeeded) {
            this.refreshNeeded = false;
            this.refresh();
          }
        }
      }
    }

    renderCodes(cart) {
      this.codes.replaceChildren();
      var seen = [];
      cart.discount_codes.forEach(entry => {
        if (!entry.applicable || seen.some(code => sameCode(code, entry.code))) return;
        seen.push(entry.code);
        var item = document.createElement('li');
        item.className = 'km-cart-discount__code';
        var label = document.createElement('span');
        label.textContent = entry.code;
        var remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.kmDiscountRemove = entry.code;
        remove.className = 'km-cart-discount__remove';
        remove.title = 'Remove discount ' + entry.code;
        remove.setAttribute('aria-label', remove.title);
        remove.textContent = '\u00d7';
        remove.disabled = this.pending;
        item.append(label, remove);
        this.codes.append(item);
      });
      this.codes.hidden = !seen.length;
      var savings = this.querySelector('[data-km-cart-savings]');
      if (savings && seen.length) savings.hidden = false;
    }

    async refresh() {
      if (!this.active) return;
      if (this.pending) { this.refreshNeeded = true; return; }
      var revision = ++this.readRevision;
      var lifecycle = this.lifecycle;
      try {
        var cart = await this.service.read();
        if (!this.active || lifecycle !== this.lifecycle || revision !== this.readRevision || this.pending) return;
        this.renderCodes(cart);
      } catch (error) {
        if (!this.active || lifecycle !== this.lifecycle || revision !== this.readRevision || this.pending) return;
        this.message(error.message, true);
      }
    }

    async perform(action, code) {
      if (!this.active || this.pending) return;
      try { code = normalizedCode(code); }
      catch (error) { this.message(error.message, true); return; }
      var form = this.closest('form');
      var nativeCart = form && form.kmCartForm;
      var operation = nativeCart && nativeCart.beginOperation && nativeCart.beginOperation('discount');
      var succeeded = false;
      var lifecycle = this.lifecycle;
      var service = this.service;
      var result;
      this.readRevision++;
      this.operationChanged = false;
      this.setPending(true);
      this.message(action === 'remove' ? 'Removing discount...' : 'Applying discount...', false);
      try {
        result = await service[action](code);
        if (!this.active || lifecycle !== this.lifecycle) return;
        this.renderCodes(result.cart);
        succeeded = true;
        if (result.status === 'invalid') {
          this.message('This discount code cannot be applied to your cart.', true);
        } else if (result.status === 'duplicate') {
          this.message('This discount code is already applied.', false);
        } else {
          this.message(result.status === 'removed' ? 'Discount code removed.' : 'Discount code applied.', false);
          if (action === 'apply') this.input.value = '';
        }
      } catch (error) {
        if (!this.active || lifecycle !== this.lifecycle) return;
        this.message(error.message, true);
      } finally {
        if (this.active && lifecycle === this.lifecycle) {
          if (this.operationChanged) {
            var detail = {source: 'km-cart-discount'};
            if (result) detail.cart = result.cart;
            document.dispatchEvent(new root.CustomEvent('cart:build'));
            document.dispatchEvent(new root.CustomEvent('cart:updated', {detail: detail}));
            try {
              if (nativeCart && nativeCart.cartBuildPromise) await nativeCart.cartBuildPromise;
            } catch (error) {
              succeeded = false;
              if (this.active && lifecycle === this.lifecycle) this.blockRefresh();
            }
          }
          if (this.active && lifecycle === this.lifecycle) {
            this.setPending(false);
            if (this.refreshNeeded) {
              this.refreshNeeded = false;
              this.refresh();
            }
          }
        }
        if (operation) nativeCart.finishOperation(operation, succeeded);
      }
    }
  }

  root.customElements.define('km-cart-discount', CartDiscount);
  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.querySelectorAll) event.target.querySelectorAll('km-cart-discount').forEach(element => element.deactivate());
  });
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelectorAll) event.target.querySelectorAll('km-cart-discount').forEach(element => element.activate());
  });
})(typeof window !== 'undefined' ? window : globalThis);
